import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

interface PhaseStage {
  label: string;
  color: string;
}

export interface PhaseSimulationProps {
  size: number;
  stages: PhaseStage[];
  animated: boolean;
}

export function PhaseSimulation({ size, stages, animated }: PhaseSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);
      if (stages.length === 0) return;

      const center = size / 2;
      const radius = size * 0.35;
      const nodeRadius = size * 0.08;
      
      const speed = 0.02;
      const activePhaseIndex = animated ? Math.floor((frameCount * speed) % stages.length) : 0;
      const progress = animated ? (frameCount * speed) % 1 : 0; // 0 to 1 between nodes

      // Draw connections
      for (let i = 0; i < stages.length; i++) {
        const nextI = (i + 1) % stages.length;
        
        const angle1 = (i * Math.PI * 2) / stages.length - Math.PI / 2;
        const angle2 = (nextI * Math.PI * 2) / stages.length - Math.PI / 2;
        
        const x1 = center + Math.cos(angle1) * radius;
        const y1 = center + Math.sin(angle1) * radius;
        const x2 = center + Math.cos(angle2) * radius;
        const y2 = center + Math.sin(angle2) * radius;
        
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        
        // Highlight active path
        if (animated && i === activePhaseIndex) {
          ctx.strokeStyle = stages[i].color;
          ctx.lineWidth = 4;
          // Draw a small pulse traveling along line
          const px = x1 + (x2 - x1) * progress;
          const py = y1 + (y2 - y1) * progress;
          ctx.stroke();
          
          ctx.beginPath();
          ctx.arc(px, py, 4, 0, Math.PI*2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
        } else {
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      // Draw Nodes
      for (let i = 0; i < stages.length; i++) {
        const angle = (i * Math.PI * 2) / stages.length - Math.PI / 2;
        const x = center + Math.cos(angle) * radius;
        const y = center + Math.sin(angle) * radius;
        const stage = stages[i];
        
        const isActive = animated && i === activePhaseIndex;
        
        ctx.beginPath();
        ctx.arc(x, y, isActive ? nodeRadius * 1.2 : nodeRadius, 0, Math.PI * 2);
        ctx.fillStyle = stage.color;
        if (isActive) {
          ctx.shadowBlur = 15;
          ctx.shadowColor = stage.color;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
        
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw Label
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        
        // Offset label outside the ring
        const lx = center + Math.cos(angle) * (radius + nodeRadius * 2);
        const ly = center + Math.sin(angle) * (radius + nodeRadius * 2);
        ctx.fillText(stage.label, lx, ly);
      }
    },
    [size, stages, animated]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
